// One in-flight submit per form (FR-016): repeated presses while pending send nothing, `pending`
// drives the progress state and `error` the message. The hook never touches the form's values, so a
// connection failure keeps what was typed.
import { useCallback, useEffect, useRef, useState } from "react";

import { type AppError, isAppError } from "@/api/errors";

import { reportError } from "./reportError";

export type SubmitResult<R> =
  | { status: "done"; value: R }
  | { status: "failed"; error: AppError }
  /** A press while a submit was already in flight. */
  | { status: "skipped" };

const UNKNOWN: AppError = {
  kind: "unknown",
  status: null,
  code: null,
  requestId: null,
  fieldErrors: [],
  retryAfterSeconds: null,
  allowedClients: null,
};

export function useSubmitAction<A extends unknown[], R>(
  action: (...args: A) => Promise<R>,
) {
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = useCallback(
    async (...args: A): Promise<SubmitResult<R>> => {
      if (inFlight.current) return { status: "skipped" };
      inFlight.current = true;
      setPending(true);
      setError(null);
      try {
        const value = await action(...args);
        return { status: "done", value };
      } catch (thrown) {
        // Anything that is not an AppError is a bug: report it (scrubbed) and show the generic message.
        if (!isAppError(thrown)) reportError(thrown);
        const failure = isAppError(thrown) ? thrown : UNKNOWN;
        if (mounted.current) setError(failure);
        return { status: "failed", error: failure };
      } finally {
        inFlight.current = false;
        if (mounted.current) setPending(false);
      }
    },
    [action],
  );

  const clearError = useCallback(() => setError(null), []);

  return { submit, pending, error, clearError };
}
